import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Briefcase, Target, Plus, Trash2, CheckCircle, Clock, Lock, TrendingUp, Star, Compass, BookOpen, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CareerPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [goals, setGoals] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [tips, setTips] = useState([]);
  const [testQuestions, setTestQuestions] = useState([]);
  const [exploration, setExploration] = useState({});
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [activeTab, setActiveTab] = useState('goals');
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    deadline: '',
    milestones: []
  });
  const [newMilestone, setNewMilestone] = useState('');
  
  // Test state
  const [testAnswers, setTestAnswers] = useState({});
  const [testCompleted, setTestCompleted] = useState(false);
  const [testScore, setTestScore] = useState(null);
  
  // Guide/Exploration detail view
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [selectedExploration, setSelectedExploration] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      fetchContent();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchContent = async () => {
    try {
      const [goalsRes, tipsRes, reflectionsRes, testRes, explorationRes, guidesRes, testResultRes] = await Promise.all([
        axios.get(`${API}/api/career/goals`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/career/tips`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/career/reflections`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/career/test`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/career/exploration`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/career/guides`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/career/test/result`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      setGoals(goalsRes.data);
      setTips(tipsRes.data);
      setReflections(reflectionsRes.data);
      setTestQuestions(testRes.data);
      setExploration(explorationRes.data);
      setGuides(guidesRes.data);
      
      if (testResultRes.data) {
        setTestAnswers(testResultRes.data.answers || {});
        setTestScore(testResultRes.data.score);
        setTestCompleted(true);
      }
    } catch (error) {
      console.error('Error fetching career data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addGoal = async () => {
    if (!newGoal.title.trim()) return;
    try {
      await axios.post(`${API}/api/career/goals`, newGoal, { headers: getAuthHeader() });
      setNewGoal({ title: '', description: '', deadline: '', milestones: [] });
      setShowGoalForm(false);
      fetchContent();
      toast.success('Mål tillagt!');
    } catch (error) {
      console.error('Error adding goal:', error);
    }
  };

  const toggleMilestone = async (goalId, milestoneIndex) => {
    try {
      await axios.put(`${API}/api/career/goals/${goalId}/milestone/${milestoneIndex}`, {}, { headers: getAuthHeader() });
      fetchContent();
    } catch (error) {
      console.error('Error toggling milestone:', error);
    }
  };

  const deleteGoal = async (id) => {
    try {
      await axios.delete(`${API}/api/career/goals/${id}`, { headers: getAuthHeader() });
      fetchContent();
      toast.success('Mål borttaget');
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const addMilestoneToNewGoal = () => {
    if (!newMilestone.trim()) return;
    setNewGoal({
      ...newGoal,
      milestones: [...newGoal.milestones, { text: newMilestone, done: false }]
    });
    setNewMilestone('');
  };

  const handleTestAnswer = (questionId, value) => {
    setTestAnswers({ ...testAnswers, [questionId]: value });
  };

  const submitTest = async () => {
    const totalScore = Object.values(testAnswers).reduce((sum, val) => sum + val, 0);
    const maxScore = testQuestions.length * 5;
    const percentage = Math.round((totalScore / maxScore) * 100);
    
    try {
      await axios.post(`${API}/api/career/test`, {
        answers: testAnswers,
        score: percentage
      }, { headers: getAuthHeader() });
      
      setTestScore(percentage);
      setTestCompleted(true);
      toast.success('Test sparat!');
    } catch (error) {
      console.error('Error saving test:', error);
    }
  };

  const resetTest = () => {
    setTestAnswers({});
    setTestCompleted(false);
    setTestScore(null);
  };

  const getTestInterpretation = (score) => {
    if (score >= 80) return { text: "Du verkar trivas utmärkt på ditt jobb! Fortsätt så och var tacksam för det du har.", color: "text-emerald-600", bg: "bg-emerald-50" };
    if (score >= 60) return { text: "Du har en okej situation men det finns utrymme för förbättring. Fundera på vad som saknas.", color: "text-blue-600", bg: "bg-blue-50" };
    if (score >= 40) return { text: "Du verkar inte helt nöjd. Det kan vara dags att aktivt söka förändring – internt eller externt.", color: "text-amber-600", bg: "bg-amber-50" };
    return { text: "Din situation verkar ohållbar. Överväg seriöst att söka något nytt – ditt välmående är viktigare än jobbet.", color: "text-red-600", bg: "bg-red-50" };
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="career-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl font-light">Karriär & Arbete</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl">Uppgradera för tillgång</h2>
            <p className="text-muted-foreground">Karriärverktyg ingår i premium.</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full">Uppgradera nu</Button>
          </div>
        </main>
      </div>
    );
  }

  // Guide detail view
  if (selectedGuide) {
    return (
      <div className="min-h-screen bg-background" data-testid="career-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedGuide(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Karriärguide</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-blue-50 rounded-2xl p-8">
            <BookOpen className="w-10 h-10 text-blue-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{selectedGuide.title}</h2>
            <p className="text-muted-foreground">{selectedGuide.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Steg för steg</h3>
            <ol className="space-y-3">
              {selectedGuide.steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
            <p className="text-amber-800 text-sm">
              💡 <strong>Tips:</strong> {selectedGuide.tips}
            </p>
          </div>

          {/* Contact */}
        </main>
      </div>
    );
  }

  // Exploration detail view
  if (selectedExploration) {
    const section = exploration[selectedExploration];
    return (
      <div className="min-h-screen bg-background" data-testid="career-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedExploration(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Utforska dig själv</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-purple-50 rounded-2xl p-8">
            <Compass className="w-10 h-10 text-purple-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{section.title}</h2>
            <p className="text-muted-foreground">{section.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Reflektera över dessa frågor</h3>
            {section.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-purple-50/50">
                <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium flex-shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          <p className="text-sm text-muted-foreground text-center">
            Skriv gärna ner dina svar i Anteckningar för att komma tillbaka till dem.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="career-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-light">Karriär & Arbete</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Intro */}
        <div className="text-center space-y-3">
          <Briefcase className="w-12 h-12 mx-auto text-primary" />
          <h2 className="font-heading text-2xl font-semibold">Professionell utveckling</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Sätt mål, utforska din potential och ta kontroll över din karriär.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 justify-center flex-wrap">
          {[
            { id: 'goals', label: 'Mål', icon: Target },
            { id: 'test', label: 'Trivseltest', icon: Star },
            { id: 'explore', label: 'Utforska', icon: Compass },
            { id: 'guides', label: 'Guider', icon: BookOpen },
            { id: 'tips', label: 'Tips', icon: TrendingUp },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className="rounded-full"
              size="sm"
            >
              <tab.icon className="w-4 h-4 mr-1" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* GOALS TAB */}
        {activeTab === 'goals' && (
          <>
            {!showGoalForm && (
              <Button onClick={() => setShowGoalForm(true)} className="w-full rounded-xl py-6" variant="outline">
                <Plus className="w-5 h-5 mr-2" />
                Nytt karriärmål
              </Button>
            )}

            {showGoalForm && (
              <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4">
                <h3 className="font-heading text-lg font-medium">Nytt karriärmål</h3>
                
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                  placeholder="Vad vill du uppnå? (t.ex. Bli teamledare)"
                  className="w-full p-4 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                />

                <textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                  placeholder="Varför är detta mål viktigt för dig?"
                  className="w-full p-4 rounded-xl border border-border/50 bg-background resize-none"
                  rows={2}
                />

                <div>
                  <label className="block text-sm font-medium mb-2">Deadline (valfritt)</label>
                  <input
                    type="date"
                    value={newGoal.deadline}
                    onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                    className="w-full p-4 rounded-xl border border-border/50 bg-background"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Delmål (steg på vägen)</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newMilestone}
                      onChange={(e) => setNewMilestone(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addMilestoneToNewGoal()}
                      placeholder="t.ex. Ta ledarskapskurs"
                      className="flex-1 p-3 rounded-xl border border-border/50 bg-background"
                    />
                    <Button onClick={addMilestoneToNewGoal} variant="outline" className="rounded-xl px-4" data-testid="add-milestone-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Lägg till
                    </Button>
                  </div>
                  {newGoal.milestones.length > 0 && (
                    <div className="space-y-2">
                      {newGoal.milestones.map((m, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-secondary/30 rounded-lg text-sm">
                          <span className="flex-1">{m.text}</span>
                          <button onClick={() => setNewGoal({...newGoal, milestones: newGoal.milestones.filter((_, idx) => idx !== i)})} className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button onClick={addGoal} className="flex-1 rounded-xl">Spara mål</Button>
                  <Button onClick={() => setShowGoalForm(false)} variant="outline" className="rounded-xl">Avbryt</Button>
                </div>
              </div>
            )}

            {/* Goals List */}
            <section className="space-y-4">
              {loading ? (
                <div className="space-y-4">{[1, 2].map(i => <div key={i} className="h-48 bg-secondary/50 rounded-2xl animate-pulse" />)}</div>
              ) : goals.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Inga karriärmål satta än. Skapa ditt första!</p>
              ) : (
                <div className="space-y-4">
                  {goals.map((goal) => {
                    const completedMilestones = goal.milestones?.filter(m => m.done).length || 0;
                    const totalMilestones = goal.milestones?.length || 0;
                    const progress = totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;

                    return (
                      <div key={goal.id} className="bg-card rounded-2xl border border-border/50 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="font-heading text-lg font-medium">{goal.title}</h4>
                            {goal.description && <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>}
                            {goal.deadline && (
                              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Deadline: {new Date(goal.deadline).toLocaleDateString('sv-SE')}
                              </p>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteGoal(goal.id)} className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {totalMilestones > 0 && (
                          <>
                            <div className="w-full h-2 bg-secondary rounded-full mb-2 overflow-hidden">
                              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                            </div>
                            <p className="text-xs text-muted-foreground mb-4">{completedMilestones} av {totalMilestones} delmål klara</p>
                            <div className="space-y-2">
                              {goal.milestones.map((milestone, mIndex) => (
                                <button
                                  key={mIndex}
                                  onClick={() => toggleMilestone(goal.id, mIndex)}
                                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${milestone.done ? 'bg-primary/5 border-primary/20' : 'bg-secondary/30 border-border/50 hover:bg-secondary/50'}`}
                                >
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${milestone.done ? 'bg-primary border-primary' : 'border-muted-foreground/50'}`}>
                                    {milestone.done && <CheckCircle className="w-3 h-3 text-primary-foreground" />}
                                  </div>
                                  <span className={`flex-1 text-sm ${milestone.done ? 'line-through text-muted-foreground' : ''}`}>{milestone.text}</span>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {/* TEST TAB - Trivs du där du är? */}
        {activeTab === 'test' && (
          <section className="space-y-6">
            <div className="bg-amber-50 rounded-2xl p-6 text-center">
              <Star className="w-10 h-10 mx-auto text-amber-600 mb-3" />
              <h3 className="font-heading text-xl font-semibold mb-2">Trivseltest: Är du på rätt plats?</h3>
              <p className="text-muted-foreground text-sm">Svara ärligt på frågorna för att få en bild av hur du mår på jobbet.</p>
            </div>

            {testCompleted && testScore !== null ? (
              <div className="space-y-4">
                <div className={`rounded-2xl p-6 ${getTestInterpretation(testScore).bg}`}>
                  <div className="text-center mb-4">
                    <span className={`text-4xl font-bold ${getTestInterpretation(testScore).color}`}>{testScore}%</span>
                    <p className="text-sm text-muted-foreground mt-1">Din trivselpoäng</p>
                  </div>
                  <p className={`text-center ${getTestInterpretation(testScore).color}`}>{getTestInterpretation(testScore).text}</p>
                </div>
                <Button onClick={resetTest} variant="outline" className="w-full rounded-xl">Gör testet igen</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {testQuestions.map((q, i) => (
                  <div key={q.id} className="bg-white rounded-xl border border-border/30 p-4">
                    <p className="font-medium mb-3">{i + 1}. {q.text}</p>
                    <div className="flex gap-2 justify-between">
                      {[1, 2, 3, 4, 5].map(val => (
                        <button
                          key={val}
                          onClick={() => handleTestAnswer(q.id, val)}
                          className={`flex-1 py-2 rounded-lg text-sm transition-colors ${testAnswers[q.id] === val ? 'bg-primary text-white' : 'bg-secondary/50 hover:bg-secondary'}`}
                        >
                          {val === 1 ? 'Stämmer inte' : val === 5 ? 'Stämmer helt' : val}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                
                <Button 
                  onClick={submitTest} 
                  className="w-full rounded-xl py-6"
                  disabled={Object.keys(testAnswers).length < testQuestions.length}
                >
                  Visa resultat ({Object.keys(testAnswers).length}/{testQuestions.length} besvarade)
                </Button>
              </div>
            )}
          </section>
        )}

        {/* EXPLORE TAB - Vad vill jag bli? */}
        {activeTab === 'explore' && (
          <section className="space-y-4">
            <div className="bg-purple-50 rounded-2xl p-6 text-center">
              <Compass className="w-10 h-10 mx-auto text-purple-600 mb-3" />
              <h3 className="font-heading text-xl font-semibold mb-2">Vad vill jag bli?</h3>
              <p className="text-muted-foreground text-sm">Utforska dina styrkor, intressen och värderingar för att hitta rätt väg.</p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {Object.entries(exploration).map(([key, section]) => (
                <button
                  key={key}
                  onClick={() => setSelectedExploration(key)}
                  className="bg-white rounded-xl border border-border/30 p-5 text-left hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{section.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* GUIDES TAB */}
        {activeTab === 'guides' && (
          <section className="space-y-4">
            <div className="bg-blue-50 rounded-2xl p-6 text-center">
              <BookOpen className="w-10 h-10 mx-auto text-blue-600 mb-3" />
              <h3 className="font-heading text-xl font-semibold mb-2">Karriärguider</h3>
              <p className="text-muted-foreground text-sm">Steg-för-steg-guider för att nå dina karriärmål.</p>
            </div>

            <div className="space-y-3">
              {guides.map((guide) => (
                <button
                  key={guide.id}
                  onClick={() => setSelectedGuide(guide)}
                  className="w-full bg-white rounded-xl border border-border/30 p-5 text-left hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{guide.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{guide.description}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* TIPS TAB */}
        {activeTab === 'tips' && (
          <section className="grid gap-4 md:grid-cols-2">
            {tips.map((tip, index) => (
              <div key={tip.id} className="bg-card rounded-2xl border border-border/50 p-6">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <TrendingUp className="w-4 h-4 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">{tip.title}</h4>
                <p className="text-sm text-muted-foreground">{tip.description}</p>
              </div>
            ))}
          </section>
        )}

        {/* Contact */}
        
        <p className="text-xs text-muted-foreground text-center">
          ⚕️ Karriärframgång handlar om att hitta meningsfullhet och balans, inte bara prestation.
        </p>
      </main>
    </div>
  );
}
